import { useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  Filler,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(LineElement, PointElement, Filler, CategoryScale, LinearScale, Tooltip, Legend);
const EMPTY_BINS = [];
const BIN_COUNT = 1024;
const STEP_HZ = 24000 / 1024;

// 고정값: 48 kHz 샘플레이트, fftSize=1024 → Nyquist= 24 kHz
export default function SpectrumGraph({
  data,
  frequneyRange,
  setValues,
  isNearHotspot,
}) {
  // 고정값: 96 kHz 샘플레이트, fftSize=1024 → Nyquist=48 kHz

  const bins = data?.bins ? data?.bins : EMPTY_BINS;
  const hotspotWeight = getHotspotWeight(data?.hotspotDistance ?? (isNearHotspot ? 0 : Infinity));

  const shiftBins = useMemo(() => {
    const temp = new Array(BIN_COUNT);
    for (let i = 0; i < BIN_COUNT; i++) {
      temp[i] = {
        x: (i * STEP_HZ) / 1000, // kHz 단위
        y: Math.max(0, (bins[i] + 150) / 5), // 음수 방지 + 스케일링
      };
    }
    return temp;
  }, [bins]);

  // bandingHz 결과
  const { points, avgdB, peakdB, peakHz } = useMemo(
    () => bandingHz(shiftBins, frequneyRange, hotspotWeight),
    [shiftBins, frequneyRange, hotspotWeight],
  );

  useEffect(() => {
    if (avgdB && !isNaN(avgdB)) {
      setValues({ avgdB, peakdB, peakHz });
    }
  }, [avgdB, peakdB, peakHz, setValues]);

  const chartData = {
    datasets: [
      {
        label: "Amplitude",
        data: points,
        borderColor: "#2474df",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: "#ffffff",
        tension: 0,
        fill: true,
        backgroundColor: ({ chart }) => {
          const area = chart.chartArea;
          if (!area) return "rgba(36,116,223,0.12)";
          const gradient = chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
          gradient.addColorStop(0, "rgba(36,116,223,0.38)");
          gradient.addColorStop(1, "rgba(36,116,223,0.01)");
          return gradient;
        },
      },
    ],
  };

  const options = {
    parsing: { xAxisKey: "x", yAxisKey: "y" },
    animation: false,
    interaction: { mode: "nearest", axis: "x", intersect: false },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: 48, // kHz (48k 샘플레이트의 Nyquist)
        title: { display: true, text: "주파수 (kHz)", color: "#59718b" },
        ticks: { stepSize: 12, color: "#59718b", font: { size: 10 } },
        grid: { color: "rgba(112,145,184,0.08)" },
        border: { display: false },
      },
      y: {
        min: 0,
        max: 80, // 필요시 조정
        title: { display: true, text: "진폭", color: "#59718b" },
        ticks: { stepSize: 20, color: "#59718b", font: { size: 10 } },
        grid: { color: "rgba(112,145,184,0.1)" },
        border: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#ffffff", titleColor: "#193456", bodyColor: "#2474df", padding: 12, displayColors: false,
        callbacks: {
          label: (ctx) => {
            const { x, y } = ctx.raw || {};
            // 원래 dBFS로 보려면 -90
            const dbfs = typeof y === "number" ? y.toFixed(1) : y;
            return `f: ${x?.toFixed(2)} kHz, ${dbfs} dBFS`;
          },
        },
      },
    },
  };

  return (
    <div className="sample-spectrum-plot">
      <Line data={chartData} options={options} role="img" aria-label={data?.bins?.length ? `주파수 스펙트럼. 검출 ${avgdB} dB, 최대 ${peakdB} dB, 최대 주파수 ${peakHz} kHz` : "주파수 스펙트럼: 측정 데이터 대기"} />
      {!data?.bins?.length && <div className="sample-chart-empty">측정 지점을 선택하면<br />스펙트럼이 표시됩니다.</div>}
    </div>
  );
}

function getHotspotWeight(distance) {
  if (distance > 70) return 1;
  if (distance <= 30) return 1.35;
  const [outer, inner, low, high] = distance > 45
    ? [70, 45, 1.1, 1.25]
    : [45, 30, 1.25, 1.35];
  const t = (outer - distance) / (outer - inner);
  return low + (high - low) * t * t * (3 - 2 * t);
}

function bandingHz(shiftBins, frequencyRange, hotspotWeight) {
  let startX = 0;
  let endX = 48;
  let multiple = 1;
  let sum = 0;
  let sumCnt = 0;
  let maxY = 0;
  let maxX = 0;

  switch (frequencyRange) {
    case "audible":
      startX = 0;
      multiple = 1;
      endX = 10;
      break;
    case "gas":
      startX = 2;
      multiple = 5;
      endX = 6.5;
      break;
    case "elec":
      startX = 2.5;
      multiple = 10;
      break;
    default:
      startX = 0;
      multiple = 1;
      break;
  }

  let points = shiftBins.map((p) => ({ ...p })); // 그대로 사용 (곱하지 않음)

  for (let index = 0; index < points.length; index++) {
    points[index].x = points[index].x * multiple;
    if (
      points[index].x < startX * multiple ||
      points[index].x > endX * multiple
    ) {
      points[index].y = 0;
      continue;
    }
    if (
      frequencyRange === "gas" &&
      hotspotWeight > 1 &&
      points[index].x > 19 &&
      points[index].x < 20
    ) {
      points[index].y *= hotspotWeight;
    }

    if (points[index].y > maxY) {
      maxY = points[index].y;
      maxX = points[index].x;
    }
    sum += points[index].y * points[index].y;
    sumCnt++;
  }

  let avgdB = sumCnt > 0 ? Math.sqrt(sum / sumCnt).toFixed(1) : 0;
  maxY = maxY.toFixed(1);
  maxX = maxX.toFixed(1);

  return { points, avgdB, peakdB: maxY, peakHz: maxX };
}
